// AGC: FarmProHub Main Menu Component
// AGC: Main app shell with fixed TopNav/BottomNav and dynamic center content

import React, { useState, useCallback } from 'react'
import { TopNav } from './TopNav'
import { BottomNav, BottomNavKey } from './BottomNav'
import { Splash } from './Splash'
import { Home } from './Home'
import { StakingPage } from './StakingPage'
import { ProposalsPage } from './ProposalsPage'
import { RevenuesPage } from './RevenuesPage'
import { DocsPage } from './DocsPage'
import { AnalyticsPage } from './AnalyticsPage'
import { ForumPage } from './ForumPage'
import { WalletFundsPage } from './WalletFundsPage'

// AGC: Page types for navigation
export type ActivePage = 'home' | 'staking' | 'proposals' | 'revenues' | 'docs' | 'analytics' | 'forum' | 'walletFunds'

export const MainMenu: React.FC = () => {
  const [activePage, setActivePage] = useState<ActivePage>('home')
  const [activeNav, setActiveNav] = useState<BottomNavKey>('forum')
  const [showSplash, setShowSplash] = useState(true)
  const [docsInitialSection, setDocsInitialSection] = useState<string>('overview')
  const [docsInitialExpandedEntry, setDocsInitialExpandedEntry] = useState<number | null>(null)

  // AGC: Map page to nav item
  const pageToNav = useCallback((page: ActivePage): BottomNavKey => {
    if (page === 'forum') return 'forum'
    if (page === 'proposals') return 'proposals'
    if (page === 'revenues') return 'revenues'
    if (page === 'docs') return 'docs'
    if (page === 'analytics') return 'analytics'
    return 'forum' // Default to forum for home
  }, [])

  // AGC: Navigate to a page
  const navigateToPage = useCallback((page: ActivePage | string, options?: { section?: string; expandedEntry?: number | null }) => {
    const activePage = page as ActivePage
    setActivePage(activePage)
    setActiveNav(pageToNav(activePage))
    
    // AGC: Handle docs page navigation with specific section/entry
    if (activePage === 'docs' && options) {
      if (options.section) {
        setDocsInitialSection(options.section)
      }
      if (options.expandedEntry !== undefined) {
        setDocsInitialExpandedEntry(options.expandedEntry)
      }
    } else if (activePage === 'docs') {
      // AGC: Reset to defaults when navigating to docs normally
      setDocsInitialSection('overview')
      setDocsInitialExpandedEntry(null)
    }
    
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pageToNav])

  // AGC: Handle splash completion
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false)
    navigateToPage('home')
  }, [navigateToPage])

  // AGC: Handle bottom nav click
  const handleNavClick = useCallback((navId: BottomNavKey) => {
    setShowSplash(false)
    setActiveNav(navId)
    
    // AGC: Map nav to page
    const navToPage: Record<BottomNavKey, ActivePage> = {
      'forum': 'forum',
      'proposals': 'proposals',
      'revenues': 'revenues',
      'docs': 'docs',
      'analytics': 'analytics',
    }
    
    navigateToPage(navToPage[navId])
  }, [navigateToPage])

  return (
    <>
      {/* AGC: Persistent app container with fixed top/bottom nav */}
      <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
        {/* AGC: Fixed Top Navbar */}
        <TopNav 
          onOpenStaking={() => navigateToPage('staking')}
          onOpenWalletFunds={() => navigateToPage('walletFunds')}
        />

        {/* AGC: Splash Screen (shows on initial load) */}
        {showSplash && activePage === 'home' && (
          <Splash onComplete={handleSplashComplete} fullScreen={false} />
        )}

        {/* AGC: Home Dashboard */}
        {!showSplash && activePage === 'home' && (
          <Home onNavigateToPage={navigateToPage} />
        )}

        {/* AGC: Staking Page */}
        {activePage === 'staking' && (
          <StakingPage />
        )}

        {/* AGC: Proposals Page */}
        {activePage === 'proposals' && (
          <ProposalsPage />
        )}

        {/* AGC: Revenues Page */}
        {activePage === 'revenues' && (
          <RevenuesPage />
        )}

        {/* AGC: Docs Page */}
        {activePage === 'docs' && (
          <DocsPage 
            initialSection={docsInitialSection}
            initialExpandedEntry={docsInitialExpandedEntry}
          />
        )}

        {/* AGC: Analytics Page */}
        {activePage === 'analytics' && (
          <AnalyticsPage />
        )}

        {/* AGC: Forum Page */}
        {activePage === 'forum' && (
          <ForumPage />
        )}

        {/* AGC: Wallet Funds Page */}
        {activePage === 'walletFunds' && (
          <WalletFundsPage onBack={() => navigateToPage('home')} />
        )}

        {/* AGC: Fixed Bottom Navigation */}
        <BottomNav onNavClick={handleNavClick} activeNav={activeNav} />
      </div>
    </>
  )
}

